"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ItemsLoader = void 0;
const react_hooks_1 = require("@apollo/react-hooks");
const BasicFieldType_1 = __importDefault(require("@opsbase/lib-data-fieldtype/BasicFieldType"));
const MetaModelRegistry_1 = __importDefault(require("@opsbase/lib-data-metamodel/MetaModelRegistry"));
const ModelRegistry_1 = __importDefault(require("@opsbase/lib-data-model/ModelRegistry"));
const QueryFieldRegistry_1 = __importDefault(require("@opsbase/lib-data-queryfield/QueryFieldRegistry"));
const DateRanges_1 = __importDefault(require("@opsbase/lib-time/DateRanges"));
const useView_1 = __importDefault(require("@opsbase/web-ctrl-view/useView"));
const graphql_tag_1 = __importDefault(require("graphql-tag"));
const react_1 = require("react");
const useDataTree_1 = __importDefault(require("../useDataTree"));
const useDataTreeActions_1 = __importDefault(require("../useDataTreeActions"));
const EMPTY_ARRAY = [];
function ItemsLoader(props) {
    var _a, _b;
    const { nodeKey, noSkipping = false, pageSize } = props;
    const dt = (0, useDataTree_1.default)();
    const prevLoaded = (_b = (_a = dt.nodesByKey.get(nodeKey)) === null || _a === void 0 ? void 0 : _a.itemCollection) === null || _b === void 0 ? void 0 : _b.lastLoaded;
    const { setItemCollectionForNode } = (0, useDataTreeActions_1.default)();
    const view = (0, useView_1.default)(dt.viewId);
    const node = dt.nodesByKey.get(nodeKey);
    const baseFilter = dt.baseFilter;
    const groupsToFilter = dt.groupsForNode.get(node);
    const skip = !noSkipping && !node.expanded && !node.highlighted;
    // -------------------------
    // PREPARE
    // -------------------------
    // Get details of View we want to query items for
    const { type, fields, sortBy, sortByDesc, filters, highlightedDateRange, fieldsChanged, filtersChanged, sortingChanged, pinningChanged, groupsChanged, } = view;
    const { filterables, queryName, alwaysGetGqlFields } = MetaModelRegistry_1.default[type];
    const model = ModelRegistry_1.default[type];
    // -------------------------
    // INITIALISE THE GQL STATEMENT
    // -------------------------
    // Initialize the string bits
    let gqlQueryDefs = '';
    let gqlQueryArgs = '';
    // Initialize the Variables Object
    // Adding the CHANGED flags forces useQuery to requery if they change
    const variables = {
        groupsChanged,
        fieldsChanged,
        filtersChanged,
        sortingChanged,
        pinningChanged,
    };
    // -------------------------`
    // ADD EMPTY QUERY PARAMETERS
    // -------------------------
    // The Query will need EMPTY filters for everything that we don't specifically add
    // query values to
    // First lets add a an array of ALL possible filterable queryfields and add our BASE FILTER to it
    const allFilters = filterables.map(f => ({ queryFieldKey: f.key }));
    if (baseFilter)
        allFilters.push(baseFilter);
    // Now we loop through these to set up the GQL string, and add an empty value into the Variables list,
    allFilters.forEach(filter => {
        // create the query def
        const fieldType = QueryFieldRegistry_1.default[filter.queryFieldKey].fieldType;
        const filterType = fieldType === BasicFieldType_1.default.DATE ? 'ISODate' : 'String';
        gqlQueryDefs += '$' + filter.queryFieldKey + ': [' + filterType + '], ';
        // include the query arg
        gqlQueryArgs += filter.queryFieldKey + ': $' + filter.queryFieldKey + ', ';
        // Add Empty Variables
        variables[filter.queryFieldKey] = [];
    });
    // -------------------------
    // HANDLE OUR POSITION IN THE DATA TREE
    // -------------------------
    // First we build a set of filters for our position in the Group Tree
    // (as specified by groupTreeLeafKey)
    let groupFilters = [];
    if (groupsToFilter) {
        groupFilters = groupsToFilter.map(group => group);
    }
    // And, we filter out any all conflicting filters
    const otherFilters = filters.filter(fb => !groupFilters.find(dfb => dfb.queryFieldKey === fb.queryFieldKey));
    // Giving us a list of applicable filters
    const applicableFilters = [...groupFilters, ...otherFilters];
    if (baseFilter)
        applicableFilters.push(baseFilter);
    // -------------------------
    // APPLY OUR APPLICABLE FILTERS
    // -------------------------
    applicableFilters.forEach(fb => {
        var _a;
        if (!variables[fb.queryFieldKey]) {
            const filterFT = QueryFieldRegistry_1.default[fb.queryFieldKey].fieldType;
            const filterType = filterFT === BasicFieldType_1.default.DATE ? 'ISODate' : 'String';
            gqlQueryDefs += '$' + fb.queryFieldKey + ': [' + filterType + '], ';
            // include the query arg
            gqlQueryArgs += fb.queryFieldKey + ': $' + fb.queryFieldKey + ', ';
            // Add Empty Variables
            variables[fb.queryFieldKey] = [];
        }
        const fieldType = (_a = QueryFieldRegistry_1.default[fb.queryFieldKey]) === null || _a === void 0 ? void 0 : _a.fieldType;
        // Date Filters
        if (fieldType === BasicFieldType_1.default.DATE) {
            if (fb.interval) {
                const dateRange = Object.values(DateRanges_1.default).find(d => d.interval === fb.interval);
                fb.from = dateRange.from().tz('UTC');
                fb.to = dateRange.to(fb.from).tz('UTC');
            }
            variables[fb.queryFieldKey].push(fb.from.toISOString(true));
            variables[fb.queryFieldKey].push(fb.to.toISOString(true));
        }
        // Boolean Filters
        else if (fieldType === BasicFieldType_1.default.BOOLEAN) {
            variables[fb.queryFieldKey].push(fb.id);
        }
        // String Filters
        else {
            variables[fb.queryFieldKey].push(fb.id);
        }
    });
    // Apply Highlighted Date Range filter if necessary
    if (highlightedDateRange === null || highlightedDateRange === void 0 ? void 0 : highlightedDateRange.key) {
        variables[highlightedDateRange.key] = [
            highlightedDateRange.from.tz('UTC').toISOString(),
            highlightedDateRange.to.tz('UTC').toISOString(),
        ];
    }
    // -------------------------
    // ADD PAGINATION
    // -------------------------
    const dataPage = node.page;
    gqlQueryDefs += '$offset: Int, $limit: Int, ';
    gqlQueryArgs += 'offset: $offset, limit: $limit, ';
    variables['offset'] = dataPage * pageSize;
    variables['limit'] = pageSize;
    // -------------------------
    // ADD SORTING
    // -------------------------
    if (sortBy) {
        gqlQueryDefs += '$sortBy: String, $sortByDesc: Boolean, ';
        gqlQueryArgs += 'sortBy: $sortBy, sortByDesc: $sortByDesc, ';
        variables['sortBy'] = sortBy.key;
        variables['sortByDesc'] = sortByDesc;
    }
    // -------------------------
    // DETERMINE WHICH FIELDS WE WANT TO REQUEST
    // -------------------------
    let gqlFields = 'id, ' + (alwaysGetGqlFields ? alwaysGetGqlFields + ', ' : '');
    let gqlFragments = '';
    const gqlType = new model().__typename;
    fields
        // Only care about fields that require data gathering
        .filter(field => !!field.gqlFields)
        // Sort the fields so we don't reload when the column order is changed
        .sort((a, b) => a.key.localeCompare(b.key))
        // Create fragments for the fields
        .forEach(field => {
        // create a fragment
        const fragmentName = 'fragment_' + type + '_' + field.key;
        gqlFragments +=
            'fragment ' +
                fragmentName +
                ' ' +
                'on ' +
                gqlType +
                ' { ' +
                field.gqlFields +
                ' }\n';
        // include the fragment
        gqlFields += '...' + fragmentName + ',\n';
    });
    // -------------------------
    // PULL ALL OF THE GQL TOGETHER
    // -------------------------
    if (gqlQueryDefs)
        gqlQueryDefs = '(' + gqlQueryDefs + ')';
    if (gqlQueryArgs)
        gqlQueryArgs = '(' + gqlQueryArgs + ')';
    const gqlStr = (0, graphql_tag_1.default) `
			${gqlFragments}
			query ${queryName.toUpperCase()} ${gqlQueryDefs} {
				${queryName} ${gqlQueryArgs} {
					offset,
					limit,
					totalCount,
					items { ${gqlFields} }
				}
			}`;
    // -------------------------
    // PERFORM THE QUERY
    // -------------------------
    const { loading, data, refetch } = (0, react_hooks_1.useQuery)(gqlStr, {
        variables,
        fetchPolicy: 'network-only',
        skip,
    });
    // -------------------------
    // HANDLE THE RESPONSE
    // -------------------------
    const result = loading ? undefined : data === null || data === void 0 ? void 0 : data[queryName];
    (0, react_1.useEffect)(() => {
        let lastLoaded = skip || !loading ? prevLoaded : new Date();
        let totalCount = 0;
        let items = EMPTY_ARRAY;
        if (!loading && !!result) {
            totalCount = result.totalCount;
            items = result.items.map(i => new model(i));
        }
        setItemCollectionForNode(node, {
            loading,
            lastLoaded,
            totalCount,
            items,
        });
    }, [skip, loading, result, model, node, setItemCollectionForNode, prevLoaded, refetch]);
    return null;
}
exports.ItemsLoader = ItemsLoader;
exports.default = ItemsLoader;
